import { Copy, Check, Home, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import { Container } from '@app/components/Container';
import { PageHeader } from '@app/components/PageHeader';
import BreadcrumbNav from '@app/components/BreadcrumbNav';
import { useCopyToClipboard } from '@app/hooks/useCopyToClipboard';

import { FileTable } from './components/FileTable';
import { FileCard } from './components/FileCard';
import { FileNavigationControls } from './components/FileNavigationControls';
import { useFileBrowser } from './hooks/useFileBrowser';

export default function BrowsePage() {
  const {
    contents,
    currentPath,
    isLoading,
    error,
    canGoBack,
    canGoForward,
    sortOrder,
    navigateTo,
    goBack,
    goForward,
    getParentDirectory,
    isDirectoryBrowsable,
    refreshDirectory,
    cycleSortOrder,
  } = useFileBrowser();

  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Create data URL from the current path
  const getCurrentDirectoryUrl = () => {
    const formattedPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
    return `https://data.jua.ai${formattedPath}`;
  };

  // Handle copying the current directory URL
  const handleCopyCurrentDirectory = () => {
    const url = getCurrentDirectoryUrl();
    copyToClipboard(url, 'current-directory');
  };

  return (
    <Container>
      <PageHeader
        title="File Browser"
        description="Browse and access files from Jua's data server"
      />

      <div className="space-y-4 mt-6">
        {/* Desktop navigation bar */}
        <div className="hidden md:block bg-white dark:bg-gray-800 shadow rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BreadcrumbNav path={currentPath} onNavigate={navigateTo} />
              <button
                onClick={handleCopyCurrentDirectory}
                className="ml-4 text-[#E34290] hover:text-[#E34290]/80 dark:text-[#E34290] dark:hover:text-[#E34290]/80 inline-flex items-center text-sm font-medium transition-colors duration-200"
                aria-label="Copy current directory URL"
                data-ph-capture-attribute="btn-browse-copy-current-directory-url"
              >
                {isCopied('current-directory') ? (
                  <>
                    <Check className="h-4 w-4 mr-1 text-green-600 dark:text-green-400" />
                    <span className="text-green-600 dark:text-green-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    <span>Copy URL</span>
                  </>
                )}
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <FileNavigationControls
                canGoBack={canGoBack}
                canGoForward={canGoForward}
                onBack={goBack}
                onForward={goForward}
                onRefresh={refreshDirectory}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Mobile navigation controls */}
        <div className="block md:hidden bg-white dark:bg-gray-800 shadow rounded-t-lg p-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigateTo('/')}
                className={`p-2 rounded transition-colors duration-200 ${
                  currentPath === '/'
                    ? 'text-gray-400 dark:text-gray-600'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                disabled={currentPath === '/'}
                aria-label="Home"
                data-ph-capture-attribute="btn-browse-home"
              >
                <Home className="h-4 w-4" />
              </button>
              <button
                onClick={goBack}
                className={`p-2 rounded transition-colors duration-200 ${
                  !canGoBack
                    ? 'text-gray-400 dark:text-gray-600'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                disabled={!canGoBack}
                aria-label="Back"
                data-ph-capture-attribute="btn-browse-back"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={goForward}
                className={`p-2 rounded transition-colors duration-200 ${
                  !canGoForward
                    ? 'text-gray-400 dark:text-gray-600'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                disabled={!canGoForward}
                aria-label="Forward"
                data-ph-capture-attribute="btn-browse-forward"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handleCopyCurrentDirectory}
                className="p-2 rounded text-[#E34290] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-label="Copy path URL"
                data-ph-capture-attribute="btn-browse-copy-current-directory-path"
              >
                {isCopied('current-directory') ? (
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={cycleSortOrder}
                className="p-2 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-label="Toggle sort order"
                data-ph-capture-attribute="btn-browse-toggle-sort-order"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {sortOrder === 'asc' ? (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <polyline points="19 12 12 19 5 12" />
                    </>
                  ) : (
                    <>
                      <line x1="12" y1="19" x2="12" y2="5" />
                      <polyline points="5 12 12 5 19 12" />
                    </>
                  )}
                </svg>
              </button>
              <button
                onClick={refreshDirectory}
                className={`p-2 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                  isLoading ? 'animate-spin' : ''
                }`}
                aria-label="Refresh"
                data-ph-capture-attribute="btn-browse-refresh-directory"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Error display */}
        {error ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center text-red-500 dark:text-red-400 border border-gray-200 dark:border-gray-700">
            <p className="mb-2 font-medium">Error loading content</p>
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Desktop view */}
            <div className="hidden md:block bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <FileTable
                contents={contents}
                currentPath={currentPath}
                isLoading={isLoading}
                sortOrder={sortOrder}
                isDirectoryBrowsable={isDirectoryBrowsable}
                navigateTo={navigateTo}
                getParentDirectory={getParentDirectory}
                onToggleSortOrder={cycleSortOrder}
                data-ph-capture-attribute="btn-browse-table"
              />
            </div>

            {/* Mobile view */}
            <div className="block md:hidden bg-white dark:bg-gray-800 shadow rounded-b-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <FileCard
                contents={contents}
                currentPath={currentPath}
                isLoading={isLoading}
                isDirectoryBrowsable={isDirectoryBrowsable}
                navigateTo={navigateTo}
                getParentDirectory={getParentDirectory}
                data-ph-capture-attribute="btn-browse-card"
              />
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
